import { useState, type FC } from "react";
import { Outlet, Navigate, Link, useLocation } from "react-router-dom";
import {
  HomeLine,
  Users01,
  Car01,
  Tool01,
  Calendar,
  Package,
  Archive,
  Receipt,
  BarChart07,
  LogOut01,
  Menu01,
  XClose,
  Settings01,
} from "@untitledui/icons";
import { useAuth } from "@/context/AuthProvider";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { cx } from "@/utils/cx";

interface NavItem {
  name: string;
  href: string;
  icon: FC<{ className?: string }>;
  disabled?: boolean;
}

const navigation: NavItem[] = [
  { name: "Dashboard", href: "/dashboard", icon: HomeLine },
  { name: "Customers", href: "/customers", icon: Users01 },
  { name: "Vehicles", href: "/vehicles", icon: Car01 },
  { name: "Jobs", href: "/jobs", icon: Tool01 },
  { name: "Appointments", href: "/appointments", icon: Calendar, disabled: true },
  { name: "Inventory", href: "/inventory", icon: Package, disabled: true },
  { name: "Storage", href: "/storage", icon: Archive, disabled: true },
  { name: "Invoices", href: "/invoices", icon: Receipt },
  { name: "Reports", href: "/reports", icon: BarChart07 },
];

export default function DashboardLayout() {
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const userInitials = user
    ? `${user.firstName?.charAt(0) ?? ""}${user.lastName?.charAt(0) ?? ""}`
    : "";

  const isActive = (href: string) =>
    href === "/dashboard"
      ? location.pathname === "/dashboard"
      : location.pathname.startsWith(href);

  return (
    <div className="flex h-screen bg-bg-primary">
      {/* Desktop Sidebar */}
      <aside className="hidden xl:flex print:!hidden w-64 border-r border-border-secondary bg-bg-primary flex-col">
        <div className="p-6 border-b border-border-secondary">
          <h1 className="text-lg font-semibold text-text-primary">My Garage</h1>
        </div>

        <nav className="flex-1 p-4 flex flex-col">
          <div className="flex-1 space-y-1">
            {navigation.map((item) =>
              item.disabled ? (
                <span
                  key={item.href}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-text-disabled cursor-not-allowed"
                >
                  <item.icon className="size-5 shrink-0 text-fg-disabled" />
                  {item.name}
                  <span className="ml-auto text-[10px] uppercase tracking-wider text-text-disabled">Soon</span>
                </span>
              ) : (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cx(
                    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    isActive(item.href)
                      ? "bg-bg-active text-text-primary"
                      : "text-text-secondary hover:bg-bg-secondary_hover hover:text-text-primary"
                  )}
                >
                  <item.icon
                    className={cx(
                      "size-5 shrink-0",
                      isActive(item.href) ? "text-fg-brand-primary" : "text-fg-quaternary"
                    )}
                  />
                  {item.name}
                </Link>
              )
            )}
          </div>
          <Link
            to="/settings"
            className={cx(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
              isActive("/settings")
                ? "bg-bg-active text-text-primary"
                : "text-text-secondary hover:bg-bg-secondary_hover hover:text-text-primary"
            )}
          >
            <Settings01
              className={cx(
                "size-5 shrink-0",
                isActive("/settings") ? "text-fg-brand-primary" : "text-fg-quaternary"
              )}
            />
            Settings
          </Link>
        </nav>

        <div className="p-4 border-t border-border-secondary">
          <div className="flex items-center gap-3">
            <Avatar size="sm" initials={userInitials} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-text-tertiary truncate">
                {user?.email}
              </p>
            </div>
            <Button
              size="sm"
              color="tertiary"
              iconLeading={LogOut01}
              onClick={logout}
              aria-label="Logout"
            />
          </div>
        </div>
      </aside>

      {/* Mobile top bar + Main content wrapper */}
      <div className="flex flex-1 flex-col min-w-0">
        <header className="flex items-center justify-between border-b border-border-secondary bg-bg-primary px-4 py-3 xl:hidden print:!hidden">
          <h1 className="text-lg font-semibold text-text-primary">My Garage</h1>
          <Button
            size="sm"
            color="tertiary"
            iconLeading={mobileMenuOpen ? XClose : Menu01}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          />
        </header>

        {/* Slide-over menu (full screen on mobile, side panel on iPad) */}
        {mobileMenuOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/50 hidden md:block"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="fixed inset-0 z-50 bg-bg-primary flex flex-col md:inset-y-0 md:left-0 md:right-auto md:w-72 md:border-r md:border-border-secondary">
              <div className="flex items-center justify-between p-4 border-b border-border-secondary">
                <h1 className="text-lg font-semibold text-text-primary">My Garage</h1>
                <Button
                  size="sm"
                  color="tertiary"
                  iconLeading={XClose}
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Close menu"
                />
              </div>

              <nav className="flex-1 p-4 flex flex-col overflow-y-auto">
                <div className="flex-1 space-y-1">
                  {navigation.map((item) =>
                    item.disabled ? (
                      <span
                        key={item.href}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-text-disabled cursor-not-allowed"
                      >
                        <item.icon className="size-5 shrink-0 text-fg-disabled" />
                        {item.name}
                        <span className="ml-auto text-[10px] uppercase tracking-wider text-text-disabled">Soon</span>
                      </span>
                    ) : (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={cx(
                          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                          isActive(item.href)
                            ? "bg-bg-active text-text-primary"
                            : "text-text-secondary hover:bg-bg-secondary_hover hover:text-text-primary"
                        )}
                      >
                        <item.icon
                          className={cx(
                            "size-5 shrink-0",
                            isActive(item.href) ? "text-fg-brand-primary" : "text-fg-quaternary"
                          )}
                        />
                        {item.name}
                      </Link>
                    )
                  )}
                </div>
                <Link
                  to="/settings"
                  onClick={() => setMobileMenuOpen(false)}
                  className={cx(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                    isActive("/settings")
                      ? "bg-bg-active text-text-primary"
                      : "text-text-secondary hover:bg-bg-secondary_hover hover:text-text-primary"
                  )}
                >
                  <Settings01
                    className={cx(
                      "size-5 shrink-0",
                      isActive("/settings") ? "text-fg-brand-primary" : "text-fg-quaternary"
                    )}
                  />
                  Settings
                </Link>
              </nav>

              <div className="p-4 border-t border-border-secondary">
                <div className="flex items-center gap-3">
                  <Avatar size="sm" initials={userInitials} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-text-tertiary truncate">
                      {user?.email}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    color="tertiary"
                    iconLeading={LogOut01}
                    onClick={logout}
                    aria-label="Logout"
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Main content */}
        <main className="flex-1 overflow-auto print:!overflow-visible">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
